import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';

type AvaylerClient = 'BSRO' | 'MYCAR';

type ClientContextProps = {
  client: AvaylerClient | null;
  handleChangeClient: (serviceLocation: LocationModel | null) => void;
};

const clients: Record<string, AvaylerClient> = {
  'United States': 'BSRO',
  Australia: 'MYCAR',
};

const ClientContext = createContext<ClientContextProps | undefined>(undefined);

export const ClientProvider = ({ children }: PropsWithChildren) => {
  const [client, setClient] = useState<AvaylerClient | null>(null);

  const handleChangeClient = useCallback(
    (serviceLocation: LocationModel | null) => {
      if (!serviceLocation) {
        setClient(null);
        return;
      }

      const country = serviceLocation.address?.country;
      setClient(country ? (clients[country] ?? null) : null);
    },
    []
  );

  const providerValue = useMemo(
    () => ({
      client,
      handleChangeClient,
    }),
    [client, handleChangeClient]
  );

  return (
    <ClientContext.Provider value={providerValue}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClientContext = (): ClientContextProps => {
  const context = useContext(ClientContext);

  if (context === undefined) {
    throw new Error('useClientContext must be used within a ClientProvider');
  }

  return context;
};
